import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { api, ApiError } from "../lib/api";
import type { BetChanges } from "../lib/api";
import { useLive } from "../lib/use-live";
import { useAuth } from "../context/auth";
import { useDateTime } from "../lib/use-relative";
import { useT } from "../context/locale";
import type { TranslationKey } from "../i18n";
import { useToast } from "../context/toast";
import {
  centsToInput,
  fromLocalInput,
  money,
  odds,
  parseMoney,
  signedMoney,
  toLocalInput,
} from "../lib/format";
import {
  ActionButton,
  ActionIcon,
  BetStatusBadge,
  BetTypeBadge,
  Button,
  EmptyState,
  ErrorNote,
  LoadingRows,
  Modal,
  NoRowActions,
  Panel,
  RowActions,
  Table,
  Td,
  TestBadge,
  Tr,
} from "../components/ui";
import {
  DateTimeField,
  FieldGrid,
  MoneyField,
  NumberField,
  SelectField,
  TextAreaField,
} from "../components/ui/form";
import {
  FilterBar,
  PageBar,
  SortHeader,
  useFilters,
  useSort,
} from "../components/ui/filters";
import type { Bet, BetType, GameRules, Paged } from "../types/api";

const BET_TYPES: BetType[] = ["number", "big", "small", "odd", "even"];

export function Bets() {
  const dateTime = useDateTime();
  const t = useT();
  const { can } = useAuth();
  const [params] = useSearchParams();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const filters = useFilters(
    {
      username: "",
      drawNo: params.get("drawNo") ?? "",
      status: "",
      excludeTest: "",
      from: "",
      to: "",
    },
    () => setPage(1),
  );
  const sort = useSort("createdAt", "desc", () => setPage(1));

  const bets = useLive<Paged<Bet>>("bets", {
        page,
        perPage,
        sort: sort.sort,
        order: sort.order,
        username: filters.applied.username || undefined,
        drawNo: filters.applied.drawNo || undefined,
        status: filters.applied.status || undefined,
        excludeTest: filters.applied.excludeTest || undefined,
        from: filters.applied.from
          ? new Date(filters.applied.from).toISOString()
          : undefined,
        to: filters.applied.to
          ? new Date(`${filters.applied.to}T23:59:59`).toISOString()
          : undefined,
      });

  const [editing, setEditing] = useState<Bet | null>(null);
  const [deleting, setDeleting] = useState<Bet | null>(null);

  return (
    <div className="space-y-5">
      <FilterBar
        loading={bets.loading}
        state={filters}
        filters={[
          {
            key: "username",
            label: t("players.filter.player"),
            placeholder: t("players.filter.playerPlaceholder"),
          },
          {
            key: "drawNo",
            label: t("bets.filter.roundNumber"),
            type: "number",
            placeholder: "e.g. 134160",
          },
          {
            key: "status",
            label: t("common.status"),
            type: "select",
            options: [
              { value: "", label: t("common.anyStatus") },
              ...(["placed", "won", "lost", "refunded", "voided"] as const).map(
                (v) => ({ value: v, label: t(`betStatus.${v}` as TranslationKey) }),
              ),
            ],
          },
          { key: "from", label: t("bets.filter.placedFrom"), type: "date" },
          { key: "to", label: t("bets.filter.placedTo"), type: "date" },
        ]}
      />

      <Panel
        eyebrow={t("nav.group.money")}
        title={t("bets.title")}
        bodyClassName=""
        actions={
          <Button size="sm" onClick={bets.reload}>
            {t("action.refresh")}
          </Button>
        }
      >
        {bets.error ? (
          <div className="p-5">
            <ErrorNote message={bets.error} onRetry={bets.reload} />
          </div>
        ) : (
          <>
            <Table
              head={[
                <SortHeader key="d" field="drawNo" state={sort}>
                  {t("common.round")}
                </SortHeader>,
                t("common.player"),
                <SortHeader key="p" field="position" state={sort}>
                  {t("col.pos")}
                </SortHeader>,
                <SortHeader key="t" field="betType" state={sort}>
                  {t("col.bet")}
                </SortHeader>,
                <SortHeader
                  key="s"
                  field="stakeCents"
                  state={sort}
                  align="right"
                >
                  {t("col.stake")}
                </SortHeader>,
                t("games.odds"),
                t("col.result"),
                <SortHeader key="st" field="status" state={sort}>
                  {t("common.status")}
                </SortHeader>,
                <SortHeader
                  key="pay"
                  field="payoutCents"
                  state={sort}
                  align="right"
                >
                  {t("col.payout")}
                </SortHeader>,
                <SortHeader key="c" field="createdAt" state={sort}>
                  {t("col.placed")}
                </SortHeader>,
                <span key="a" className="block text-right">
                  {t("col.actions")}
                </span>,
              ]}
            >
              {bets.loading ? (
                <LoadingRows columns={11} />
              ) : bets.data && bets.data.items.length === 0 ? (
                <Tr>
                  <Td colSpan={11}>
                    <EmptyState
                      title={t("bets.empty")}
                      detail={t("sessions.emptyDetail")}
                    />
                  </Td>
                </Tr>
              ) : (
                (bets.data?.items ?? []).map((bet) => (
                  <Tr key={bet.id}>
                    <Td className="money accent text-[12px]">#{bet.drawNo}</Td>
                    <Td>
                      {bet.user ? (
                        <Link
                          to={`/players/${bet.user.id}`}
                          className="flex items-center gap-1.5 hover:underline"
                        >
                          {bet.user.username}
                          {bet.user.isTest ? <TestBadge /> : null}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </Td>
                    <Td className="money">{bet.position}</Td>
                    <Td>
                      <BetTypeBadge type={bet.betType} pickedNumber={bet.pickedNumber} />
                    </Td>
                    <Td className="money">{money(bet.stakeCents)}</Td>
                    <Td className="money text-dim text-[12px]">
                      {odds(bet.oddsBasisPoints)}
                    </Td>
                    <Td className="money">{bet.resultNumber ?? "—"}</Td>
                    <Td>
                      <BetStatusBadge status={bet.status} />
                    </Td>
                    <Td
                      className="money"
                      style={{
                        color:
                          bet.payoutCents > 0
                            ? "var(--success)"
                            : "var(--text-dim)",
                      }}
                    >
                      {bet.payoutCents > 0 ? money(bet.payoutCents) : "—"}
                    </Td>
                    <Td className="text-dim text-[12px]">
                      {dateTime(bet.createdAt)}
                    </Td>
                    <Td>
                      {can("admin") ? (
                        <RowActions>
                          {/* Won and lost bets stay editable — correcting one
                              re-judges it and moves the balance. Only a refunded
                              or voided bet is beyond changing, and it is shown
                              disabled rather than dropped: a control that
                              vanishes on some rows makes the column look
                              unreliable, where a locked one explains itself. */}
                          <ActionButton
                            icon={ActionIcon.edit}
                            disabledWhy={
                              ["refunded", "voided"].includes(bet.status)
                                ? t("bets.cannotEditSettled")
                                : null
                            }
                            onClick={() => setEditing(bet)}
                          >
                            {t("action.edit")}
                          </ActionButton>
                          <ActionButton
                            icon={ActionIcon.delete}
                            tone="danger"
                            onClick={() => setDeleting(bet)}
                          >
                            {t("action.remove")}
                          </ActionButton>
                        </RowActions>
                      ) : (
                        <NoRowActions reason={t("action.noPermission")} />
                      )}
                    </Td>
                  </Tr>
                ))
              )}
            </Table>

            {bets.data ? (
              <PageBar
                page={bets.data.page}
                pages={bets.data.pages}
                total={bets.data.total}
                perPage={perPage}
                onPage={setPage}
                onPerPage={(size) => {
                  setPerPage(size);
                  setPage(1);
                }}
              />
            ) : null}
          </>
        )}
      </Panel>

      <EditBetDialog
        bet={editing}
        onClose={() => setEditing(null)}
        onDone={bets.reload}
      />
      <RemoveBetDialog
        bet={deleting}
        onClose={() => setDeleting(null)}
        onDone={bets.reload}
      />
    </div>
  );
}

/**
 * Edits a bet: which position it is on, which side it took, or how much it is for.
 *
 * Any of the three may change at once. If the round already has numbers the API
 * re-judges the bet immediately and settles the difference, so correcting a
 * mis-keyed selection on a finished round moves the player's balance here too.
 */
function EditBetDialog({
  bet,
  onClose,
  onDone,
}: {
  bet: Bet | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const rules = useLive<GameRules>("rules", {});
  const [position, setPosition] = useState("");
  const [betType, setBetType] = useState<BetType>("big");
  const [picked, setPicked] = useState("");
  const [stake, setStake] = useState("");
  const [placedAt, setPlacedAt] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the bet's current values once, when the dialog is handed a bet.
  useEffect(() => {
    if (!bet) return;
    setPosition(String(bet.position));
    setBetType(bet.betType);
    setPicked(bet.pickedNumber === null ? "" : String(bet.pickedNumber));
    setStake(centsToInput(bet.stakeCents));
    setPlacedAt(toLocalInput(bet.createdAt));
    setReason("");
    setError(null);
  }, [bet]);

  const positionCount = rules.data?.positionCount ?? 0;
  const parsedStake = parseMoney(stake);
  const parsedPosition = Number.parseInt(position, 10);
  const positionValid =
    Number.isInteger(parsedPosition) &&
    parsedPosition >= 1 &&
    (positionCount === 0 || parsedPosition <= positionCount);
  const stakeValid = parsedStake !== null && parsedStake > 0;
  const parsedPicked = Number.parseInt(picked, 10);
  const pickedValid =
    betType !== "number" ||
    (Number.isInteger(parsedPicked) &&
      parsedPicked >= (rules.data?.numberRange.min ?? 1) &&
      parsedPicked <= (rules.data?.numberRange.max ?? 99));

  // Send only what actually changed — the API requires at least one field.
  const changes: BetChanges = {};
  if (bet && positionValid && parsedPosition !== bet.position)
    changes.position = parsedPosition;
  if (bet && betType !== bet.betType) changes.betType = betType;
  // Only a number pick carries one; switching away must clear it.
  if (betType === "number") {
    const parsed = Number.parseInt(picked, 10);
    if (Number.isInteger(parsed) && parsed !== bet?.pickedNumber) changes.pickedNumber = parsed;
  } else if (bet?.pickedNumber !== null) {
    changes.pickedNumber = null;
  }
  if (bet && stakeValid && parsedStake !== bet.stakeCents)
    changes.stakeCents = parsedStake;
  /*
   * The field holds minutes; the bet's own timestamp usually carries seconds.
   * Comparing the two as instants would therefore read as a change on every
   * open, so both sides are compared in the shape the field is in.
   */
  const placedAtIso = fromLocalInput(placedAt);
  const placedAtValid = placedAt === "" ? false : placedAtIso !== null;
  if (bet && placedAtValid && placedAt !== toLocalInput(bet.createdAt))
    changes.createdAt = placedAtIso ?? undefined;
  const changed = Object.keys(changes).length > 0;

  const delta = stakeValid && bet ? parsedStake - bet.stakeCents : 0;
  /** A round that has already been drawn will re-judge the moment this is saved. */
  const settled = Boolean(bet && bet.status !== "placed");

  function close() {
    setStake("");
    setPosition("");
    setPlacedAt("");
    setReason("");
    setError(null);
    onClose();
  }

  async function submit() {
    if (!bet || !changed || !reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.updateBet(bet.id, changes, reason.trim());
      const money_ =
        result.stakeDelta === 0
          ? null
          : t("bets.stakeDelta", { amount: signedMoney(result.stakeDelta) });
      toast.success(
        t("bets.updatedToast"),
        [
          money_,
          result.bet.payoutCents
            ? t("bets.nowStatusPaying", {
                status: t(`status.${result.bet.status}` as TranslationKey),
                amount: money(result.bet.payoutCents),
              })
            : t("bets.nowStatus", {
                status: t(`status.${result.bet.status}` as TranslationKey),
              }),
        ]
          .filter(Boolean)
          .join(" "),
      );
      onDone();
      close();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("bets.updateError"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(bet)}
      onClose={close}
      eyebrow={t("bets.edit.round", { drawNo: bet?.drawNo ?? "" })}
      title={t("audit.bet.update")}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="gold"
            onClick={submit}
            loading={busy}
            disabled={
              !changed ||
              !positionValid ||
              !stakeValid ||
              !placedAtValid ||
              !reason.trim()
            }
          >
            {t("action.save")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {settled ? (
          <p
            className="rounded-lg px-3 py-2 text-[12px]"
            style={{ background: "var(--warning-bg)", color: "var(--warning)" }}
          >
{t("bets.edit.drawnNote")}
          </p>
        ) : (
          <p className="text-muted text-[13px]">
{t("bets.edit.stillOpen")}
          </p>
        )}

        <FieldGrid>
          <NumberField
            label={
              positionCount
                ? t("bets.edit.positionRange", { count: positionCount })
                : t("bets.edit.position")
            }
            value={position}
            onChange={setPosition}
            error={
              position !== "" && !positionValid
                ? t("bets.edit.positionError")
                : null
            }
            required
            maxLength={2}
          />

          <SelectField
            label={t("bets.edit.betType")}
            value={betType}
            onChange={setBetType}
            options={BET_TYPES.map((v) => ({
              value: v,
              label: t(`bet.${v}` as TranslationKey),
            }))}
            required
          />
        </FieldGrid>

        {/* Only a number pick takes one, so the field appears with the type
            rather than sitting there greyed out for the other four. */}
        {betType === "number" ? (
          <NumberField
            label={
              rules.data
                ? t("bets.edit.pickedRange", {
                    min: rules.data.numberRange.min,
                    max: rules.data.numberRange.max,
                  })
                : t("bets.edit.pickedNumber")
            }
            value={picked}
            onChange={setPicked}
            error={picked !== "" && !pickedValid ? t("bets.edit.pickedError") : null}
            hint={
              rules.data
                ? t("bets.edit.numberOdds", {
                    odds: odds(rules.data.numberOddsBasisPoints ?? 0),
                  })
                : undefined
            }
            required
            maxLength={2}
          />
        ) : null}

        <MoneyField
          label={t("col.stake")}
          value={stake}
          onChange={setStake}
          error={
            !stakeValid && stake !== "" ? t("bets.edit.stakeError") : null
          }
          required
        />

        {stakeValid && delta !== 0 ? (
          <p
            className="rounded-lg px-3 py-2 text-[12px]"
            style={{
              background: delta > 0 ? "var(--danger-bg)" : "var(--success-bg)",
              color: delta > 0 ? "var(--danger)" : "var(--success)",
            }}
          >
            {t(delta > 0 ? "bets.edit.takesFurther" : "bets.edit.returns", {
              amount: money(Math.abs(delta)),
            })}
          </p>
        ) : null}

        <DateTimeField
          label={t("bets.edit.placedAt")}
          value={placedAt}
          onChange={setPlacedAt}
          error={!placedAtValid && placedAt !== "" ? t("bets.edit.placedAtError") : null}
          hint={t("bets.edit.placedAtHint")}
          required
        />

        <TextAreaField
          label={t("common.reason")}
          value={reason}
          onChange={setReason}
          placeholder={t("bets.edit.reasonPlaceholder")}
          required
        />

        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

function RemoveBetDialog({
  bet,
  onClose,
  onDone,
}: {
  bet: Bet | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close() {
    setReason("");
    setError(null);
    onClose();
  }

  async function submit() {
    if (!bet || !reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteBet(bet.id, reason.trim());
      toast.success(
        t("bets.removedToast"),
        t("bets.removedDetail"),
      );
      onDone();
      close();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("bets.removeError"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(bet)}
      onClose={close}
      eyebrow={t("bets.title")}
      title={t("audit.bet.delete")}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={submit}
            loading={busy}
            disabled={!reason.trim()}
          >
            {t("bets.remove.submit")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div
          className="rounded-lg px-4 py-3"
          style={{ background: "var(--surface-inset)" }}
        >
          <p className="eyebrow mb-2">{t("bets.remove.heading")}</p>
          <ul className="text-muted space-y-1 text-[12px]">
            <li>
              · Returns the{" "}
              <span className="money">{money(bet?.stakeCents ?? 0)}</span> stake
            </li>
            {bet && bet.payoutCents > 0 ? (
              <li>
                · Takes back the{" "}
                <span className="money">{money(bet.payoutCents)}</span> payout
              </li>
            ) : null}
            <li>· Writes both movements to the ledger and the audit log</li>
          </ul>
        </div>

        <p className="text-dim text-[12px]">
{t("bets.removeNote")}
        </p>

        <TextAreaField
          label={t("common.reason")}
          value={reason}
          onChange={setReason}
          placeholder={t("bets.remove.reasonPlaceholder")}
          required
        />

        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}
